import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { CouponType } from "@/lib/constants";
import type { Match, BetSelection, CouponBet } from "@/types/domain";

interface CouponCartState {
  type: CouponType | null;
  bets: CouponBet[];
  setType: (t: CouponType) => void;
  addBet: (match: Match, bet: BetSelection) => void;
  updateBet: (matchId: number, bet: BetSelection) => void;
  removeBet: (matchId: number) => void;
  clear: () => void;
  hasMatch: (matchId: number) => boolean;
  getBet: (matchId: number) => CouponBet | undefined;
}

export const useCouponCart = create<CouponCartState>()(
  persist(
    (set, get) => ({
      type: null,
      bets: [],
      setType: (t) =>
        set((s) => ({
          type: t,
          bets: s.bets.slice(0, getMaxForType(t)),
        })),
      addBet: (match, bet) =>
        set((s) => {
          if (!s.type) return s;
          if (s.bets.some((b) => b.matchId === match.id)) return s;
          if (s.bets.length >= getMaxForType(s.type)) return s;
          return { bets: [...s.bets, { matchId: match.id, match, bet }] };
        }),
      updateBet: (matchId, bet) =>
        set((s) => ({
          bets: s.bets.map((b) => (b.matchId === matchId ? { ...b, bet } : b)),
        })),
      removeBet: (matchId) =>
        set((s) => ({ bets: s.bets.filter((b) => b.matchId !== matchId) })),
      clear: () => set({ type: null, bets: [] }),
      hasMatch: (matchId) => get().bets.some((b) => b.matchId === matchId),
      getBet: (matchId) => get().bets.find((b) => b.matchId === matchId),
    }),
    { name: "super-pari-coupon-cart" }
  )
);

function getMaxForType(t: CouponType): number {
  switch (t) {
    case "TOP10":
      return 10;
    case "TOP20":
      return 20;
    case "TOP30":
      return 30;
  }
}
